import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../security/security/AuthContext';
import { checkUserStatus } from '../../security/api/ApiClient';
import axios from 'axios';
import { 
 FaLock, 
 FaEnvelope, 
 FaUser, 
 FaMobile, 
 FaArrowRight,
 FaUserPlus,
 FaSignInAlt,
 FaInfoCircle,
 FaShieldAlt,
 FaGlobe
} from 'react-icons/fa';
import LoadingLogin from './loaders/LoadingLogin';
import RegisterLoading from './loaders/RegisterLoading';
import WhatsAppFloat from './WhatsAppFloat';

const Auth = () => {
 const [isLogin, setIsLogin] = useState(true);
 const [loginForm, setLoginForm] = useState({ email: '', password: '' });
 const [signupForm, setSignupForm] = useState({
   fullName: '', email: '', mobile: '', password: '', confirmPassword: '', role: 'USER'
 });
 const [errors, setErrors] = useState({});
 const [loading, setLoading] = useState(false);
 const [message, setMessage] = useState('');
 const [messageType, setMessageType] = useState('');
 const [invitationCode, setInvitationCode] = useState('');
 
 const navigate = useNavigate();
 const location = useLocation();
 const authContext = useAuth();
 
 useEffect(() => {
   const params = new URLSearchParams(location.search);
   const code = params.get('invite_code');
   if (code) setInvitationCode(code);
 }, [location]);
 
 const handleLoginChange = (e) => {
   const { name, value } = e.target;
   setLoginForm({ ...loginForm, [name]: value });
 };
 
 const handleSignupChange = (e) => {
   const { name, value } = e.target;
   setSignupForm({ ...signupForm, [name]: value });
 };
 
 const validateLogin = () => {
   const errors = {};
   if (!loginForm.email) errors.email = 'Email is required';
   else if (!/\S+@\S+\.\S+/.test(loginForm.email)) errors.email = 'Email is invalid';
   if (!loginForm.password) errors.password = 'Password is required';
   return errors;
 };
 
 const validateSignup = () => {
   const errors = {};
   if (!signupForm.fullName.trim()) errors.fullName = 'Full Name is required';
   if (!signupForm.email) errors.email = 'Email is required';
   else if (!/\S+@\S+\.\S+/.test(signupForm.email)) errors.email = 'Email is invalid';
   if (!signupForm.mobile) errors.mobile = 'Mobile is required';
   else if (!/^(07|01|254)\d+$/.test(signupForm.mobile)) errors.mobile = 'Invalid phone number format';
   if (!signupForm.password) errors.password = 'Password is required';
   else if (signupForm.password.length < 6) errors.password = 'Password must be 6+ characters';
   if (!signupForm.confirmPassword) errors.confirmPassword = 'Confirm Password is required';
   else if (signupForm.confirmPassword !== signupForm.password) errors.confirmPassword = 'Passwords do not match';
   return errors;
 };
 
 const handleLoginSubmit = async (e) => {
   e.preventDefault();
   const validationErrors = validateLogin();
   if (Object.keys(validationErrors).length > 0) {
     setErrors(validationErrors);
     return;
   }
   
   setLoading(true);
   setMessage('');
   
   try {
     const result = await authContext.login(loginForm.email, loginForm.password);
     if (result.success) {
       const userStatus = await checkUserStatus();
       if (userStatus === 'ACTIVE' || userStatus === 'INACTIVE') {
         setMessage('Login successful!');
         setMessageType('success');
         setTimeout(() => navigate('/user-dashboard'), 2000);
       } else {
         setMessage('Account issue. Contact support.');
         setMessageType('error');
         authContext.logout();
       }
     } else {
       setMessage(result.message);
       setMessageType('error');
     }
   } catch (error) {
     setMessage('Login failed. Try again.');
     setMessageType('error');
   } finally {
     setLoading(false);
   }
 };
 
 const handleSignupSubmit = async (e) => {
   e.preventDefault();
   const validationErrors = validateSignup();
   if (Object.keys(validationErrors).length > 0) {
     setErrors(validationErrors);
     return;
   }
 
   setLoading(true);
   setMessage('');
 
   try {
     const response = await axios.post('https://api.globallequityfinance.com/register', {
       ...signupForm,
       invitationCode
     }, {
       headers: { 'Content-Type': 'application/json' }
     });
 
     if (response.status === 200) {
       setIsLogin(true);
       setMessage('Registration successful! Please login.');
       setMessageType('success');
       setLoginForm(prev => ({ ...prev, email: signupForm.email }));
       setSignupForm({ fullName: '', email: '', mobile: '', password: '', confirmPassword: '', role: 'USER' });
     }
   } catch (error) {
     setMessage(error.response?.data?.error || 'Registration failed. Try again.');
     setMessageType('error');
   } finally {
     setLoading(false);
   }
 };
 
 return (
   <div className="global-equity-auth">
     <div className="auth-container">
       {/* Simple Header */}
       <div className="auth-header">
         <div className="brand">
           <FaGlobe className="globe-icon" />
           <h1>Global<span>Equity</span></h1>
         </div>
         <p className="subtitle">Professional Investment Platform</p>
       </div>

       {/* Auth Card */}
       <div className="auth-card">
         {/* Tabs */}
         <div className="tab-switcher">
           <button 
             className={`tab ${isLogin ? 'active' : ''}`}
             onClick={() => {setIsLogin(true); setErrors({}); setMessage('');}}
           >
             <FaSignInAlt />
             Sign In
           </button>
           <button 
             className={`tab ${!isLogin ? 'active' : ''}`}
             onClick={() => {setIsLogin(false); setErrors({}); setMessage('');}}
           >
             <FaUserPlus />
             Sign Up
           </button>
         </div>

         {/* Invitation Banner */}
         {invitationCode && !isLogin && (
           <div className="invitation-banner">
             <FaShieldAlt />
             <span>You have an exclusive invitation!</span>
           </div>
         )}

         {/* Message */}
         {message && (
           <div className={`message ${messageType}`}>
             {message}
           </div>
         )}

         {/* Form */}
         <div className="form-container">
           {loading && (
             <div className="loading-overlay">
               {isLogin ? <LoadingLogin /> : <RegisterLoading />}
             </div>
           )}

           {isLogin ? (
             <form onSubmit={handleLoginSubmit} className="auth-form">
               <h2>Welcome Back</h2>
               <p>Access your investment account</p>

               <div className="input-group">
                 <div className="input-wrapper">
                   <FaEnvelope className="input-icon" />
                   <input
                     type="email"
                     name="email"
                     placeholder="Email Address"
                     value={loginForm.email}
                     onChange={handleLoginChange}
                     className={errors.email ? 'error' : ''}
                   />
                 </div>
                 {errors.email && <span className="error-text">{errors.email}</span>}
               </div>

               <div className="input-group">
                 <div className="input-wrapper">
                   <FaLock className="input-icon" />
                   <input
                     type="password"
                     name="password"
                     placeholder="Password"
                     value={loginForm.password}
                     onChange={handleLoginChange}
                     className={errors.password ? 'error' : ''}
                   />
                 </div>
                 {errors.password && <span className="error-text">{errors.password}</span>}
               </div>

               <button type="submit" className="submit-btn" disabled={loading}>
                 <span>Sign In</span>
                 <FaArrowRight />
               </button>
             </form>
           ) : (
             <form onSubmit={handleSignupSubmit} className="auth-form">
               <h2>Create Account</h2>
               <p>Start your investment journey</p>

               {[
                 { name: 'fullName', type: 'text', placeholder: 'Full Name', icon: <FaUser /> },
                 { name: 'email', type: 'email', placeholder: 'Email Address', icon: <FaEnvelope /> },
                 { name: 'mobile', type: 'number', placeholder: 'Mobile Number', icon: <FaMobile /> },
                 { name: 'password', type: 'password', placeholder: 'Password', icon: <FaLock /> },
                 { name: 'confirmPassword', type: 'password', placeholder: 'Confirm Password', icon: <FaLock /> }
               ].map((field) => (
                 <div key={field.name} className="input-group">
                   <div className="input-wrapper">
                     <div className="input-icon">{field.icon}</div>
                     <input
                       type={field.type}
                       name={field.name}
                       placeholder={field.placeholder}
                       value={signupForm[field.name]}
                       onChange={handleSignupChange}
                       className={errors[field.name] ? 'error' : ''}
                     />
                   </div>
                   {errors[field.name] && <span className="error-text">{errors[field.name]}</span>}
                 </div>
               ))}

               <button type="submit" className="submit-btn" disabled={loading}>
                 <span>Create Account</span>
                 <FaArrowRight />
               </button>

               <div className="info-note">
                 <FaInfoCircle />
                 <span>Join professional investors worldwide</span>
               </div>
             </form>
           )}
         </div>
       </div>
     </div>
     
     <style jsx>{`
       .global-equity-auth {
         min-height: 100vh;
         background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
         display: flex;
         align-items: center;
         justify-content: center;
         padding: 20px;
         font-family: 'Inter', system-ui, sans-serif;
       }

       .auth-container {
         max-width: 420px;
         width: 100%;
       }

       .auth-header {
         text-align: center;
         margin-bottom: 2rem;
       }

       .brand {
         display: flex;
         align-items: center;
         justify-content: center;
         gap: 12px;
         margin-bottom: 8px;
       }

       .globe-icon {
         font-size: 32px;
         color: #e74c3c;
       }

       .brand h1 {
         font-size: 2.2rem;
         font-weight: 800;
         margin: 0;
         color: white;
       }

       .brand h1 span {
         color: #e74c3c;
       }

       .subtitle {
         color: #8892b0;
         margin: 0;
         font-size: 1rem;
         font-weight: 500;
       }

       .auth-card {
         background: rgba(23, 42, 69, 0.9);
         backdrop-filter: blur(10px);
         border: 1px solid rgba(231, 76, 60, 0.2);
         border-radius: 16px;
         overflow: hidden;
         box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
       }

       .tab-switcher {
         display: flex;
         background: rgba(16, 20, 31, 0.8);
         border-bottom: 1px solid rgba(231, 76, 60, 0.1);
       }

       .tab {
         flex: 1;
         display: flex;
         align-items: center;
         justify-content: center;
         gap: 8px;
         padding: 16px;
         background: transparent;
         border: none;
         color: #8892b0;
         font-weight: 600;
         cursor: pointer;
         transition: all 0.3s ease;
       }

       .tab.active {
         background: linear-gradient(135deg, #e74c3c, #c0392b);
         color: white;
       }

       .invitation-banner {
         display: flex;
         align-items: center;
         gap: 10px;
         background: rgba(46, 204, 113, 0.1);
         border: 1px solid rgba(46, 204, 113, 0.2);
         color: #2ecc71;
         padding: 12px 20px;
         font-size: 14px;
         font-weight: 500;
       }

       .message {
         padding: 12px 20px;
         text-align: center;
         font-weight: 500;
       }

       .message.success {
         background: rgba(46, 204, 113, 0.1);
         border-bottom: 1px solid rgba(46, 204, 113, 0.2);
         color: #2ecc71;
       }

       .message.error {
         background: rgba(231, 76, 60, 0.1);
         border-bottom: 1px solid rgba(231, 76, 60, 0.2);
         color: #e74c3c;
       }

       .form-container {
         position: relative;
         padding: 2rem;
       }

       .loading-overlay {
         position: absolute;
         top: 0;
         left: 0;
         right: 0;
         bottom: 0;
         background: rgba(23, 42, 69, 0.9);
         backdrop-filter: blur(5px);
         display: flex;
         align-items: center;
         justify-content: center;
         border-radius: 12px;
         z-index: 10;
       }

       .auth-form {
         display: flex;
         flex-direction: column;
         gap: 1.2rem;
       }

       .auth-form h2 {
         font-size: 1.6rem;
         font-weight: 700;
         color: white;
         margin: 0;
         text-align: center;
       }

       .auth-form p {
         color: #8892b0;
         margin: 0 0 1rem 0;
         text-align: center;
       }

       .input-group {
         display: flex;
         flex-direction: column;
         gap: 0.5rem;
       }

       .input-wrapper {
         position: relative;
         display: flex;
         align-items: center;
       }

       .input-icon {
         position: absolute;
         left: 16px;
         color: #8892b0;
         z-index: 1;
       }

       .input-wrapper input {
         width: 100%;
         padding: 14px 14px 14px 44px;
         background: rgba(16, 20, 31, 0.8);
         border: 1px solid rgba(136, 146, 176, 0.2);
         border-radius: 10px;
         color: white;
         font-size: 15px;
         transition: all 0.3s ease;
         box-sizing: border-box;
       }

       .input-wrapper input:focus {
         outline: none;
         border-color: #e74c3c;
         box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1);
         background: rgba(16, 20, 31, 0.9);
       }

       .input-wrapper input.error {
         border-color: #e74c3c;
       }

       .input-wrapper input::placeholder {
         color: #8892b0;
       }

       .error-text {
         color: #e74c3c;
         font-size: 0.875rem;
         margin-left: 0.5rem;
       }

       .submit-btn {
         display: flex;
         align-items: center;
         justify-content: center;
         gap: 8px;
         width: 100%;
         padding: 14px;
         background: linear-gradient(135deg, #e74c3c, #c0392b);
         border: none;
         border-radius: 10px;
         color: white;
         font-size: 15px;
         font-weight: 600;
         cursor: pointer;
         transition: all 0.3s ease;
         margin-top: 0.5rem;
       }

       .submit-btn:hover:not(:disabled) {
         transform: translateY(-2px);
         box-shadow: 0 8px 25px rgba(231, 76, 60, 0.3);
       }

       .submit-btn:disabled {
         opacity: 0.7;
         cursor: not-allowed;
         transform: none;
       }

       .info-note {
         display: flex;
         align-items: center;
         gap: 10px;
         background: rgba(52, 152, 219, 0.1);
         border: 1px solid rgba(52, 152, 219, 0.2);
         border-radius: 10px;
         padding: 12px 16px;
         color: #8892b0;
         font-size: 0.9rem;
         margin-top: 0.5rem;
       }

       .info-note svg {
         color: #3498db;
       }

       /* Mobile responsiveness */
       @media (max-width: 640px) {
         .global-equity-auth {
           padding: 16px;
         }

         .auth-container {
           max-width: 100%;
         }

         .brand h1 {
           font-size: 1.8rem;
         }

         .globe-icon {
           font-size: 28px;
         }

         .form-container {
           padding: 1.5rem;
         }

         .tab {
           padding: 14px;
           font-size: 14px;
         }
       }
     `}</style>
     
     <WhatsAppFloat />
   </div>
 );
};

export default Auth;